import { PinIcon } from '@components/Icons/PinIcon';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import css from './PinButton.module.scss';

interface PinButtonProps {
  isFavorited: boolean;
  onToggle: () => void;
  /**
   * `card` (default) pins the button absolutely into a card's top-right, offset to
   * clear the card's kebab menu, and reveals it on `.cardWrapper:hover` or on keyboard
   * focus anywhere in the card. Neither fires on touch, hence no pins on touch devices.
   *
   * `inline` drops the positioning so the button can sit in a row's normal flow,
   * and is always visible: the hover reveal needs a positioned ancestor wrapping
   * the whole hover target, which a data-grid row can't safely provide.
   */
  variant?: 'card' | 'inline';
}

export function PinButton({ isFavorited, onToggle, variant = 'card' }: PinButtonProps) {
  return (
    <Tooltip
      // Just the verb, deliberately. "Pin to the top" would be a claim about position, and with
      // "Pinned first" switched off pinning doesn't move the library at all — see
      // `LibraryManager.pinnedFirst`. This wording is true in both states, and matches the
      // mobile drawer's item.
      title={isFavorited ? 'Unpin' : 'Pin'}
      placement="top"
      arrow
    >
      <button
        className={classNames(isFavorited ? css.pinButtonFavorited : css.pinButton, {
          [css.inline]: variant === 'inline',
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      >
        <span className={css.pinIconOutline}>
          <PinIcon />
        </span>
        <span className={css.pinIconFilled}>
          <PinIcon filled />
        </span>
      </button>
    </Tooltip>
  );
}

export { css as pinButtonStyles };
